/**
 * Library home.
 *
 * Deliberately thin: the two things worth putting in front of someone opening
 * an IPTV app are "carry on with what you were watching" and "go to the part of
 * the catalog you actually fetched".
 */

import { useEffect, useState } from 'react';
import type { ContinueWatchingItem, Kind, StreamDto } from '@sflx/shared';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { Icon, type IconName } from '../components/Icon';
import { Poster } from '../components/Poster';
import { catalog, state } from '../lib/api';
import { useAccounts } from '../lib/accounts-store';
import { formatClock, formatCount, progressRatio } from '../lib/format';
import { watchLink } from '../lib/links';
import { Link, navigate } from '../router/router';

/** Quick access from Home; the Live tab's Favorites filter is where they are managed. */
const FAVORITE_CHANNELS_LIMIT = 10;

export function Home() {
  const { accounts, active, loading, error } = useAccounts();
  const [items, setItems] = useState<ContinueWatchingItem[] | null>(null);
  const [favoriteChannels, setFavoriteChannels] = useState<{ accountId: string; items: StreamDto[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    state
      .continueWatching(20)
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch(() => {
        // Continue watching is a convenience rail; a failure here must not take
        // the whole library screen down with it.
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accounts.length]);

  useEffect(() => {
    if (!active?.ingest.live) {
      setFavoriteChannels(null);
      return;
    }
    const accountId = active.id;
    let cancelled = false;
    catalog
      .streams(accountId, { kind: 'live', favorites: true, limit: FAVORITE_CHANNELS_LIMIT })
      .then((result) => {
        if (!cancelled) setFavoriteChannels({ accountId, items: result.items });
      })
      .catch(() => {
        if (!cancelled) setFavoriteChannels({ accountId, items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [active?.id, active?.ingest.live]);

  if (loading && !accounts.length) {
    return (
      <div className="page">
        <Loading label="Loading your library" />
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="page">
        <ErrorNotice error={error} />
        <EmptyState
          title="Nothing here yet"
          hint="Add an Xtream Codes account and tick the parts of its catalog you want. Until you do, SFLX Live fetches nothing at all."
          action={
            <Link className="btn btn--primary" to="/accounts">
              <Icon name="plus" size={16} />
              Add an account
            </Link>
          }
        />
      </div>
    );
  }

  const entries = buildEntries(active?.ingest, active?.counts);

  return (
    <div className="page">
      <div className="wrap stack" style={{ gap: 'var(--s-7)' }}>
        <div className="page__head" style={{ marginBottom: 0 }}>
          <div className="page__title">
            <h1>{active?.label ?? 'Library'}</h1>
            <span className="muted">
              {entries.length
                ? `${entries.map((entry) => `${formatCount(entry.count)} ${entry.label.toLowerCase()}`).join(' · ')}`
                : 'Nothing fetched yet for this account.'}
            </span>
          </div>
        </div>

        {items === null ? null : items.length ? (
          <section className="stack">
            <h2>Continue watching</h2>
            <div className="rail">
              {items.map((item) => (
                <Poster
                  key={`${item.accountId}:${item.kind}:${item.id}:${item.episodeId ?? ''}`}
                  name={item.name}
                  imageUrl={item.logoUrl}
                  subtitle={
                    <>
                      {item.accountLabel} · {formatClock(item.positionS)}
                      {item.durationS ? ` / ${formatClock(item.durationS)}` : ''}
                    </>
                  }
                  corner={
                    item.durationS
                      ? `${Math.round(progressRatio(item.positionS, item.durationS) * 100)}%`
                      : undefined
                  }
                  positionS={item.positionS}
                  durationS={item.durationS}
                  onClick={() =>
                    navigate(
                      watchLink({
                        accountId: item.accountId,
                        kind: item.kind,
                        xtreamId: item.id,
                        episodeId: item.episodeId,
                        container: item.container,
                        title: item.name,
                      }),
                    )
                  }
                />
              ))}
            </div>
          </section>
        ) : null}

        {favoriteChannels?.items.length ? (
          <section className="stack">
            <h2>Favorite channels</h2>
            <div className="rail">
              {favoriteChannels.items.map((channel) => (
                <Poster
                  key={channel.id}
                  name={channel.name}
                  imageUrl={channel.logoUrl}
                  onClick={() =>
                    navigate(
                      watchLink({
                        accountId: favoriteChannels.accountId,
                        kind: 'live',
                        xtreamId: channel.id,
                        title: channel.name,
                      }),
                    )
                  }
                />
              ))}
            </div>
          </section>
        ) : null}

        <section className="stack">
          <h2>Browse</h2>
          {entries.length ? (
            <div className="grid-cards">
              {entries.map((entry) => (
                <Link key={entry.to} to={entry.to} className="card stack stack--tight" style={{ gap: 'var(--s-2)' }}>
                  <div className="row" style={{ gap: 'var(--s-3)' }}>
                    <Icon name={entry.icon} size={18} />
                    <strong>{entry.label}</strong>
                  </div>
                  <span className="muted">{formatCount(entry.count)} items</span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title="This account has no catalog yet"
              hint="Open Accounts and tick Live TV, Movies or TV Series to fetch one."
              action={
                <Link className="btn" to="/accounts">
                  Go to accounts
                </Link>
              }
            />
          )}
        </section>
      </div>
    </div>
  );
}

interface Entry {
  to: string;
  label: string;
  icon: IconName;
  count: number;
}

function buildEntries(
  ingest: { live: boolean; movies: boolean; series: boolean } | undefined,
  counts: Partial<Record<Kind, number>> | undefined,
): Entry[] {
  if (!ingest) return [];
  const entries: Entry[] = [];
  if (ingest.live) entries.push({ to: '/live', label: 'Live TV', icon: 'tv', count: counts?.live ?? 0 });
  if (ingest.movies) entries.push({ to: '/movies', label: 'Movies', icon: 'film', count: counts?.movie ?? 0 });
  if (ingest.series) entries.push({ to: '/series', label: 'Series', icon: 'stack', count: counts?.series ?? 0 });
  return entries;
}
